// /**
//  * Formats a UK date string to ISO format
//  * Returns null if the date is invalid

import { UK_DATE_FORMAT } from '@app/components/imports/utils';
import { NUMBER_TO_MONTH_MAPPING } from '@app/utils/constants';
import { ReportingYear } from '@shared/api/types';
import {
  addMonths,
  format,
  isValid,
  isWithinInterval,
  parse,
  parseISO,
  subDays,
} from 'date-fns';

// /**
//  * Formats a UK date string to ISO format
//  * Returns null if the date is invalid
//  *
//  * Handles timezone issues by treating the input date as UTC
//  *
//  * @param dateString
//  * @returns ISO date string or null
//  */
// Day-first formats accepted for client-supplied dates. Month-first (US)
// formats are deliberately excluded: "01/09/2025" is ambiguous between them,
// and silently guessing the wrong one misfiles purchases into the wrong
// reporting period.
const UK_DATE_FORMATS = [
  UK_DATE_FORMAT,
  'dd-MM-yyyy',
  'dd.MM.yyyy',
  'd MMMM yyyy',
  'd MMM yyyy',
  'yyyy-MM-dd',
];

// /**
//  * Parses a day-first date string against the accepted formats
//  * Returns null if no format matches
//  */
export const parseUKDate = (dateString: string): Date | null => {
  const trimmed = dateString?.trim();
  if (!trimmed) return null;

  for (const dateFormat of UK_DATE_FORMATS) {
    // Parse using a fixed reference date to avoid timezone issues
    const parsed = parse(trimmed, dateFormat, new Date(2000, 0, 1));
    // The year bound rejects two-digit years ("01/09/25" would otherwise
    // parse as year 0025) rather than guessing a century.
    if (isValid(parsed) && parsed.getFullYear() >= 2000) return parsed;
  }
  return null;
};

export const formatUKDateToISO = (dateString: string): string | null => {
  const parsed = parseUKDate(dateString);
  if (!parsed) return null;

  // Create a UTC date with the parsed year, month, and day
  const utcDate = new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  );

  return utcDate.toISOString();
};

// /**
//  * Validates if a date falls within the specified date range
//  */
export const isDateWithinRange = (
  dateString: string,
  startDate: string,
  endDate: string
): boolean => {
  if (!dateString?.trim() || !startDate || !endDate) return false;

  const toUtcMidnightMs = (value: string, fmt: string): number => {
    const d = parse(value, fmt, new Date()); // parsed in local tz, but we only use Y/M/D fields
    if (!isValid(d))
      throw new Error(`Invalid date: "${value}" for format "${fmt}"`);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const targetUTC = toUtcMidnightMs(dateString, 'dd/MM/yyyy');
  const startUTC = toUtcMidnightMs(startDate, 'yyyy-MM-dd');
  const endUTC = toUtcMidnightMs(endDate, 'yyyy-MM-dd');

  return isWithinInterval(targetUTC, { start: startUTC, end: endUTC });
};

/**
 * Generates a list of months based on the reporting year with date ranges
 * @param reportingYear - The reporting year object containing startDate and endDate
 * @returns Array of month strings with date ranges (e.g., ["Jun 14 - Jul 13", "Jul 14 - Aug 13", ...])
 */
export function generateMonthsForYear(reportingYear: ReportingYear): string[] {
  const startDate = parseISO(reportingYear.startDate);

  const periods: string[] = [];

  for (let i = 0; i < 12; i++) {
    const periodStart = addMonths(startDate, i);
    const periodEnd = subDays(addMonths(periodStart, 1), 1);

    const startMonth =
      NUMBER_TO_MONTH_MAPPING[String(periodStart.getMonth() + 1)];
    const endMonth = NUMBER_TO_MONTH_MAPPING[String(periodEnd.getMonth() + 1)];

    const startDay = format(periodStart, 'dd'); // e.g. "02"
    const endDay = format(periodEnd, 'dd'); // e.g. "01"

    const isSameMonth = startMonth === endMonth;
    if (isSameMonth) {
      periods.push(`${startMonth}`);
      continue;
    }

    periods.push(`${startMonth} ${startDay} - ${endMonth} ${endDay}`);
  }

  return periods;
}

/**
 * Generates month options for a time period month index based on reporting year
 * @param reportingYear - The reporting year object or string
 * @param reportingYears - Optional array of all reporting years (used when first param is a string)
 * @returns Array of month options with value and label
 */
export function generateTimePeriodMonthOptions(
  reportingYear: ReportingYear
): Array<{ value: string; label: string }> {
  if (!reportingYear) return [];

  const months = generateMonthsForYear(reportingYear);

  return months.map((month, index) => ({
    value: (index + 1).toString(),
    label: month,
  }));
}
